import { FormAPI, ProcessAPI } from 'lecom-api';
import LecomReflux from 'reflux/lecom_reflux';
import { saveAs } from 'file-saver';
import { getCompositeProperty, setCompositeProperty, serializePrivateProperties } from 'utils/object_utils';
import FileDownloaderUtils from 'utils/file_downloader_utils';
import DateUtils from 'utils/date_utils';
import { formatToNumber, formatDate,formatCurrency } from 'utils/formatting_utils';
import ManageRequests from 'utils/manage_requests';
import DocumentStoreItem from '../models/document_store_item';
import Template from '../models/template';
import DuplicatedDocument from '../models/duplicated_document';
import Document from '../models/document';
import DocumentModalIds from '../constants/document_modal_ids';
import DocumentLocks from '../constants/document_locks';
import DocumentConstants from '../constants/document';
import DocumentActionRoutes from '../constants/document_action_routes';
import DocumentSubmitDataContainers from '../constants/document_submit_data_containers';
import DocumentValidationsErrors from '../constants/document_validations_errors';
import RestClient from '../../rest_client/rest_client';
import ResponseHandler from '../../rest_client/response_handler';
import FormFieldTypes from 'form/constants/form_field_types';
import { FormEventHandler, FormEvents } from '../../form/events/form_event_handler';
import { InputDocumentEventHandler, InputDocumentEvents } from '../events/input_document_event_handler';
import { ModalActions } from '../../realize_overrides/stores/modal_store';
import DocumentMetadataValidator from '../../validator/document_metadata_validator';
import CurrentUserHandler from '../../user/handlers/current_user_handler.js';
import { GridFormStore } from '../../grid_form/reflux/grid_form_store';
import { DD_MM_YYYY_BAR, MM_DD_YYYY_BAR } from 'utils/date_utils_new.js';
import { HOST_NAME_API } from '../../../../../shared/serverInfo';
import { getCookie } from "../../utils/cookie_utils";
import { ActivityFormEventHandler, ActivityFormEvents } from 'process/events/activity_form_event_handler';

export const DocumentActions = LecomReflux.createActions([
	'init',
	'loadTemplate',
	'loadDocument',
	'downloadDocumentFile',
	'downloadMultipleFiles',
	'waitForAction',
	'setSubmitDataField',
	'setDocumentFileSelected',
	'setSubmitMetadataField',
	'setMetadataValidationErrors',
	'selectDivergentDocumentVersion',
	'setValueInDocument',
	'createDocument',
	'importDocument',
	'attachDocument',
	'detachDocument',
	'openManageDocumentModal',
	'openAttachDocumentModal',
	'openDetachDocumentModal',
	'deleteDocument',
	'checkoutDocument',
	'cancelCheckoutDocument',
	'checkinDocument',
	'changeDocumentCheckout',
	'selectDuplicateDocument',
	'overwriteDocument',
	'duplicateDocument',
	'openCreateDocumentModal',
	'openImportDocumentModal',
	'openDocumentModal',
	'closeDocumentModal',
	'displayFlashMessage',
	'dismissFlashMessage',
	'openDigitalSignature',
	'downloadDigitalSignature',
	'previewDocumentFile',
	'errorDuplicatedDocuments',
	'setMetadataFromAssociation',
	'setFileUniqueIdInDocument',
	'downloadFileAndSetupAI'
]);

export const DocumentStore = LecomReflux.createStore({
	listenables: DocumentActions,
	storeItemClass: DocumentStoreItem,
	eventHandler: FormEventHandler,
	events: FormEvents,
	store: {},

	postConstruct(storeItem) {
		if (!this.initialized) {
			this.subscribeToGridEvents();
			this.subscribeToFormEvents(storeItem);
			this.subscribeToProcessSaveEvent(storeItem);
		}

		this.onLoadTemplate(storeItem.inputId, storeItem.template.id);
	},

	/* Template data loader */

	onLoadTemplate(inputDocumentId, templateId) {
		const storeItem = this.store[inputDocumentId];
		this.performLoadTemplateActionRequest(storeItem, templateId);
	},

	performLoadTemplateActionRequest(storeItem, templateId) {
		const url = DocumentActionRoutes.GET_TEMPLATE;
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());
		const params = this.buildLoadTemplateRequestParams(processIds, { templateId });
		const findRequest = ManageRequests.findRequest('templateId', templateId);

		if (!findRequest) {
			const promise = RestClient.get(url, params).then(
				response => {
					ManageRequests.resolveRequest('templateId', templateId, response);
					this.handleLoadTemplateSuccessResponse(storeItem.inputId, response);
				},
				error => this.handleDocumentRequestErrorResponse.bind(this, storeItem.inputId)
			);

			ManageRequests.addRequest({ key: 'templateId', value: templateId, promise: promise });
		}
		else {
			(findRequest.status === "RESOLVED")
			? this.handleLoadTemplateSuccessResponse(storeItem.inputId, findRequest.response)
			: findRequest.promise.then(() => this.handleLoadTemplateSuccessResponse(storeItem.inputId, findRequest.response));
		}
	},

	buildLoadTemplateRequestParams(processIds, templateId){
		const params = Object.assign({},
			processIds,
			templateId
		);
		return params;
	},

	handleLoadTemplateSuccessResponse(inputDocumentId, response) {
		const storeItem = this.store[inputDocumentId];
		const inputProps = this.store[inputDocumentId].inputProps;
		const templateData = response.entity;
		storeItem.template = new Template(templateData);

		this._setState(inputDocumentId);

		//Para correção do bug ECM-1067 foi necessario add esse funcionalidade que atualiza a label
		//para poder atualizar o icone de do assinador digital dentro do grid
		if (inputProps.gridFormParent && templateData.pdfSigned){
			const colGrid = Form.grids(inputProps.gridFormParent).columns(inputProps.id)
			colGrid.label(colGrid.label()).apply();
		}
	},

	/* Document data loaders */

	onLoadDocumentWithValues(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const { documentLoaded, document } = storeItem;

		if (!documentLoaded && !document.isNew()) {
			storeItem.selected = true;
			this.onLoadDocument(inputDocumentId, document.fileUUID);
		}
	},

	onLoadDocument(inputDocumentId, fileUUID, modalIdToCloseAfterSelect) {
		const storeItem = this.store[inputDocumentId];
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());
		const gridName = storeItem.inputProps.gridFormParent;
		const storeGrid = gridName ? GridFormStore.store[gridName] : null;

		if (gridName && storeGrid && storeGrid.selectedRowId && storeGrid.selectedDataRow) {
			this.resetGridFormFieldValues(null, null, gridName);
			this.updateGridData(null, null, gridName, storeGrid.selectedRowId, storeGrid.selectedDataRow);
		}

		this.onWaitForAction(inputDocumentId);
		return this.performLoadDocumentActionRequest(storeItem, fileUUID, processIds, modalIdToCloseAfterSelect);
	},

	performLoadDocumentActionRequest(storeItem, fileUUID, processIds, modalIdToCloseAfterSelect) {
		const url = DocumentActionRoutes.GET_DOCUMENT;
		const params = this.buildLoadDocumentRequestParams(storeItem, fileUUID, processIds);
		const findRequest = ManageRequests.findRequest('fileUniqueId', params.fileUniqueId);

		if (!findRequest) {
			const promise = RestClient.get(url, params).then(
				response => {
					ManageRequests.resolveRequest('fileUniqueId', params.fileUniqueId, response);
					this.handleLoadDocumentSuccessResponse(storeItem.inputId, response, modalIdToCloseAfterSelect);
				},
				error => this.handleDocumentRequestErrorResponse.bind(this, storeItem.inputId)
			);

			ManageRequests.addRequest({ key: 'fileUniqueId', value: params.fileUniqueId, promise: promise });

			return promise;
		}
		else {
			if (findRequest.status === "RESOLVED") {
				this.handleLoadDocumentSuccessResponse(storeItem.inputId, findRequest.response, modalIdToCloseAfterSelect);
				return findRequest.promise;
			}
			else {
				return findRequest.promise.then(() => this.handleLoadDocumentSuccessResponse(storeItem.inputId, findRequest.response, modalIdToCloseAfterSelect));
			}
		}
	},

	buildLoadDocumentRequestParams(storeItem, fileUUID, processIds) {
		const params = Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier,
			gridRowIndex: storeItem.gridData.indexOfDocumentInEdition,
			fieldName: storeItem.inputName,
			fileUniqueId: fileUUID
		});

		return this.omitBy(params);
	},

	async handleLoadDocumentSuccessResponse(inputDocumentId, response, modalIdToCloseAfterSelect) {
		const storeItem = this.store[inputDocumentId];
		const { entity: documentData } = response;

		storeItem.document = new Document(Object.assign(documentData));
		storeItem.documentLoaded = true;
		storeItem.waitingForAction = false;
		storeItem.documentReadonly = await this.documentIsReadonly(storeItem);

		if (modalIdToCloseAfterSelect) {
			storeItem.documentUploading = false;
			storeItem.documentUploaded = true;
			this.onCloseDocumentModal(inputDocumentId, modalIdToCloseAfterSelect);
		}

		let verifyEntityValues = response.entity.fieldValues ? Object.keys(response.entity.fieldValues) : [];

		if(verifyEntityValues.length > 0)
			this.store[inputDocumentId].document.fieldValues = response.entity.fieldValues;

		this._setState(inputDocumentId);
	},

	async documentIsReadonly(storeItem) {
		const user = await CurrentUserHandler.retrieveUserByTicket();
		const { locks } = storeItem.document;

		return storeItem.document.checkedOut ? !this.documentIsLockedForEditionSameUser(locks, user) : false;
	},

	/* Document metadata values loader */

	setMetadataSubmitValuesFromAssociationValues(inputDocumentId, containerId, contextMetadata) {
		const storeItem = this.store[inputDocumentId];
		const mappingFields = storeItem.inputProps.formFieldMappings;
		const formApi = FormAPI.form(storeItem.inputProps.formId);

		const metadataFieldsAssociationValues = mappingFields.reduce((acc, cur) => {
			let fieldsModalDocument = `${cur.documentField}_IMPORT_templateField`;
			let valuesFieldForm = formApi.fields(cur.formField.id).value();

			if(valuesFieldForm !== undefined && valuesFieldForm !== null){
				let typeField = cur.formField.type;

				const fieldElem = $(`${contextMetadata ? `#${contextMetadata} ` : ''}#${fieldsModalDocument}`);

				fieldElem.val(valuesFieldForm);

				if(typeField === 'CURRENCY'){
					let currencyFormated = formatCurrency(valuesFieldForm)
					let prefix = formApi.fields(cur.formField.id).prefix() !== undefined ? formApi.fields(cur.formField.id).prefix() : Realize.i18n.t('masks.currency.prefix');
					fieldElem.val(`${prefix} ${currencyFormated}`);
				}

				if(typeField === 'DATE'){
					let dateFormated = formatDate(formApi.fields(cur.formField.id).value());
					const processAPI = ProcessAPI.currentProcess();
					const isMobile = processAPI.isMobile();

					if ((DD_MM_YYYY_BAR.test(dateFormated) || MM_DD_YYYY_BAR.test(dateFormated)) && isMobile) {
						const currentLocale = Realize.i18n.currentLocale.toLowerCase();
						const valueSplitted = dateFormated.split("/");

						if (currentLocale === 'pt-br' || currentLocale === 'es') {
							dateFormated = `${valueSplitted[2]}-${valueSplitted[1]}-${valueSplitted[0]}`;
						}
						else {
							dateFormated = `${valueSplitted[2]}-${valueSplitted[0]}-${valueSplitted[1]}`;
						}
					}

					fieldElem.val(dateFormated);
				}
			}

			return {
				...acc,
				[cur.documentField]: formApi.fields(cur.formField.id).value()
			};
		}, {});

		storeItem.metadataFieldsAssociationValues = metadataFieldsAssociationValues;
		this.setSubmitMetadataValues(inputDocumentId, containerId, metadataFieldsAssociationValues);
		// const { metadataFieldsAssociationValues } = this.store[inputDocumentId];
		// this.setSubmitMetadataValues(inputDocumentId, containerId, metadataFieldsAssociationValues);
	},

	setMetadataSubmitValuesFromFieldValues(inputDocumentId, containerId) {
		const { fieldValues } = this.store[inputDocumentId].document;

		this.setSubmitMetadataValues(inputDocumentId, containerId, fieldValues)
	},

	setSubmitMetadataValues(inputDocumentId, containerId, values) {
		const storeItem = this.store[inputDocumentId];
		const { fields } = storeItem.template;

		for (let key in values) {
			let value = values[key];

			if (fields[key].type === FormFieldTypes.CURRENCY) {
				$(`#${key}_${containerId}_templateField`).blur();
				value = formatToNumber(value);
			}


			/*if (fields[key].type === FormFieldTypes.DATE) {
				value = value ? new Date(moment.utc(value, moment.ISO_8601)) : null;
			}*/

			value = Array.isArray(value) ? value[0] : value;

			this.onSetSubmitMetadataField(inputDocumentId, containerId, key, value);
		}
	},

	/* Document file downloader */

	onDownloadDocumentFile(inputDocumentId, inputDocumentValue) {
		const storeItem = this.store[inputDocumentId];
		const processIds = ProcessAPI.currentProcess().processIds();

		let idField = `input__${inputDocumentId}`;
		let inputDoc = 'file-path';

		$(`#${idField} .${inputDoc}`).attr('tabindex', '0');

		this.performDownloadDocumentFileRequest(storeItem, processIds, inputDocumentValue);

		if (storeItem) {
			storeItem.documentDownloaded = true;
			this._setState(inputDocumentId);
		}
	},

	performDownloadDocumentFileRequest(storeItem, processIds, inputDocumentValue) {
		const currentProcess = ProcessAPI.currentProcess();
		// console.log(currentProcess.lecomBpmUrl())
		const params = this.buildDownloadDocumentFileRequestParams(storeItem, processIds, inputDocumentValue);
		const url = DocumentActionRoutes.DOWNLOAD_DOCUMENT(currentProcess.lecomBpmUrl(), processIds, params.gridIdentifier, params.fileUniqueId);
		FileDownloaderUtils.download(url, params, inputDocumentValue.fileName);
	},

	buildDownloadDocumentFileRequestParams(storeItem, processIds, inputDocumentValue) {
		var propsDocument = { };
		if (storeItem) {
			propsDocument = {
				gridIdentifier: storeItem.gridData.gridIdentifier || "",
				gridRowIndex: storeItem.gridData.indexOfDocumentInEdition || "",
				documentId: storeItem.document.id || inputDocumentValue.id || "",
				fileUniqueId: storeItem.document.fileUUID
			}
		}
		const params = Object.assign({}, processIds, propsDocument, {
			documentId: inputDocumentValue ? inputDocumentValue.id : propsDocument.documentId || "",
			fileUniqueId: inputDocumentValue ? inputDocumentValue.fileUUID : propsDocument.fileUniqueId
		});

		return this.omitBy(params);
	},

	/* Multiple Download  */

	async onDownloadMultipleFiles(documentIds, callback) {
		const currentProcess = ProcessAPI.currentProcess();
		const processIds = currentProcess.processIds();

		const url = DocumentActionRoutes.DOWNLOAD_MULTIPLE_DOCUMENTS(currentProcess.lecomBpmUrl(), processIds);

		let headers = {
			'Content-Type': 'application/json',
			'Accept': 'application/zip, application/json'
		}

		const isTestMode = CurrentUserHandler.getTestMode();
		const isPublicForm = !!currentProcess.uuid();

		if (isTestMode) {
			headers = {
				...headers,
				'test-mode': true,
				'test-user': isTestMode
			}
		}

		if (isPublicForm) {
			headers = {
				...headers,
				'form-uuid': currentProcess.uuid()
			}
		} else {
			headers = {
				...headers,
				'ticket-sso': getCookie('LecomSSOTicket')
			}
		}

		const configRequest = {
			method: 'POST',
			headers,
			body: JSON.stringify(documentIds)
		}

		try {
			const response = await fetch(url, configRequest);

			if (!response.ok) {
				const data = await response.json();

				throw new Error(JSON.stringify({
					status: response.status,
					errors: data.errors
				}));
			}

			const blob = await response.blob();

			saveAs(blob, 'arquivo.zip');

			callback({
				status: 200,
				code: 'export_multiple_success'
			});
		} catch (err) {
			const error = JSON.parse(err.message);
			callback(error);
		}
	},

	/* Control actions */

	onWaitForAction(inputDocumentId, value) {
		this._waitForAction(inputDocumentId, value);
	},

	_waitForAction(inputDocumentId, value = true) {
		const storeItem = this.store[inputDocumentId];
		storeItem.waitingForAction = value;

		return this._setState(inputDocumentId);
	},

	/* Submit data setter */

	onSetSubmitDataField(inputDocumentId, fieldKey, fieldValue) {
		const storeItem = this.store[inputDocumentId];
		const fieldApi = this.getFieldAPIForDocument(inputDocumentId);

		fieldApi.publish(FormEvents.DOCUMENT_IMPORT_FILE_SUBMITTED, fieldValue);
        setCompositeProperty(storeItem.submitData, fieldKey, fieldValue);
	},

	onSetDocumentFileSelected(inputDocumentId, selectedValue) {
		const storeItem = this.store[inputDocumentId];
		const valueIsSelected = selectedValue != null && (!Array.isArray(selectedValue) || (Array.isArray(selectedValue) && selectedValue.length > 0));

		storeItem.documentFileSelected = valueIsSelected;
		this._setState(inputDocumentId);
	},

	onSetSubmitMetadataField(inputDocumentId, containerKey, fieldKey, fieldValue) {
		const storeItem = this.store[inputDocumentId];

		fieldValue = (this.isDateAndHasValue(storeItem, fieldKey, fieldValue)) ? DateUtils.convertDateStringToISOString(fieldValue) : fieldValue;

		const metadataFieldsKey = `${containerKey}.metadata`;
		const metadataFields = getCompositeProperty(metadataFieldsKey, storeItem.submitData) || [];

		this.removeCurrentMetadataFieldEntry(metadataFields, fieldKey);
		this.pushNewMetadataFieldEntry(metadataFields, fieldKey, fieldValue);
		this.setSubmitMetadataValuesInDocumentFieldValues(metadataFields, storeItem);
		setCompositeProperty(storeItem.submitData, metadataFieldsKey, metadataFields);
	},

	isDateAndHasValue(storeItem, fieldKey, fieldValue) {
		const { fields } = storeItem.template;
		return fields[fieldKey] && fields[fieldKey].type == FormFieldTypes.DATE && fieldValue;
	},

	removeCurrentMetadataFieldEntry(metadataFields, fieldKey) {
		const currentMetadataFieldIndex = metadataFields.findIndex((field) => field.id == fieldKey);
		if(currentMetadataFieldIndex >= 0) {
			metadataFields.splice(currentMetadataFieldIndex, 1);
		}
	},

	pushNewMetadataFieldEntry(metadataFields, fieldKey, fieldValue) {
		metadataFields.push({
			id: fieldKey,
			value: fieldValue
		});
	},

	setSubmitMetadataValuesInDocumentFieldValues(metadataFields, storeItem) {
		const { document } = storeItem;

		metadataFields
			.map(metadataField => [metadataField.id, metadataField.value])
			.forEach(([fieldId, fieldValue]) => document.fieldValues[fieldId] = fieldValue);
	},

	/* Metadata validations handler */

	onSetMetadataValidationErrors(inputDocumentId, errors) {
		const storeItem = this.store[inputDocumentId];
		storeItem.metadataValidationErrors = errors;
		this._setState(inputDocumentId);
	},

	/* Create document action handlers */

	onCreateDocument(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const { fields } = storeItem.template;
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());

		if((storeItem.submitData && Object.keys(storeItem.submitData).length === 0 || !storeItem.submitData[DocumentSubmitDataContainers.CREATE].defaultType) && (storeItem._template && !storeItem._template.defaultModel)) {
			this.handleDocumentRequestOnModalErrorResponse(inputDocumentId, {entity:{errors:[{code: "required_file_type", message: "Null element"}]}});
			return;
		}

		if (DocumentMetadataValidator.validate(fields, DocumentSubmitDataContainers.CREATE, inputDocumentId)) {
			this.onWaitForAction(inputDocumentId);
			this.performCreateDocumentActionRequest(storeItem, processIds);
		}
	},

	performCreateDocumentActionRequest(storeItem, processIds) {
		const url = DocumentActionRoutes.CREATE_DOCUMENT;
		const params = this.buildCreateDocumentRequestParams(storeItem, processIds);
		storeItem.documentUploading = true;

		RestClient.post(url, params)
		.then(this.handleCreateDocumentSuccessResponse.bind(this, storeItem.inputId))
		.catch(this.handleValidationsResponseErrors.bind(this, storeItem.inputId, DocumentModalIds.CREATE_DOCUMENT));
	},

	buildCreateDocumentRequestParams(storeItem, processIds) {
		return Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier,
			fieldName: storeItem.inputName,
			data: this.buildCreateDocumentSubmitData(storeItem)
		});

	},

	buildCreateDocumentSubmitData(storeItem) {
		const containerId = DocumentSubmitDataContainers.CREATE;

		const submitData = Object.assign({}, storeItem.submitData[containerId]);
        submitData.templateId = storeItem.template.id;
        return submitData;
	},

	handleCreateDocumentSuccessResponse(inputDocumentId, response) {
		const createdDocumentData = response.entity;
		this.store[inputDocumentId].documentUploading = false;

		this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.CREATE_DOCUMENT);
		this.selectDocument(inputDocumentId, createdDocumentData);
	},

	/* Import document action handlers */

	onImportDocument(inputDocumentId, importMultiple) {
		const storeItem = this.store[inputDocumentId];
		const { fields } = storeItem.template;
		const currentProcess = ProcessAPI.currentProcess();
		const processIds = serializePrivateProperties(currentProcess.processIds());

		if (DocumentMetadataValidator.validate(fields, DocumentSubmitDataContainers.IMPORT, inputDocumentId)) {
			this.onWaitForAction(inputDocumentId);
			this.performImportDocumentActionRequest(storeItem, processIds, currentProcess.lecomBpmUrl(), importMultiple);
		}
	},

	performImportDocumentActionRequest(storeItem, processIds, lecomBpmUrl, importMultiple) {
		const params = this.buildImportDocumentRequestParams(storeItem, processIds);
		const url = DocumentActionRoutes.IMPORT_DOCUMENT(lecomBpmUrl, processIds, params.gridIdentifier, storeItem.inputId);

		this._setProperties(storeItem.inputId, {
			documentUploading: true,
			documentUploaded: false
		}, true).then(() => {
			RestClient.post(url, params.data, {multipart: true})
				.then(this.handleImportDocumentSuccessResponse.bind(this, storeItem.inputId))
				.catch(response => this.handleValidationsResponseErrors(storeItem.inputId, DocumentModalIds.IMPORT_DOCUMENT, response, importMultiple));
		});
	},

	buildImportDocumentRequestParams(storeItem, processIds) {
		const params = Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier || "",
			fieldName: storeItem.inputName,
			data: this.buildImportDocumentSubmitData(storeItem)
		});

		return this.omitBy(params);
	},

	buildImportDocumentSubmitData(storeItem) {
		let containerData = storeItem.submitData[DocumentSubmitDataContainers.IMPORT];
		containerData.metadata = Array.isArray(containerData.metadata)
			? containerData.metadata.filter(fieldData => typeof fieldData.value !== 'undefined' && fieldData.value !== null)
			: [];

		return Object.assign({}, containerData, { templateId: storeItem.template.id, fileName: containerData.file.name  });
	},

	handleImportDocumentSuccessResponse(inputDocumentId, response) {
		const responseContent = response.entity.content;
		const storeItem = this.store[inputDocumentId];

		const importedDocumentData = {
			fileName: responseContent.fileName,
			fileUUID: responseContent.fileUniqueId,
			id: responseContent.documentId,
			version: responseContent.version
		};

		this.selectDocument(inputDocumentId, importedDocumentData, DocumentModalIds.IMPORT_DOCUMENT);

		const fieldApi = this.getFieldAPIForDocument(inputDocumentId);
		fieldApi.publish(FormEvents.DOCUMENT_IMPORT_SUCCESS);
	},

	/* Attach document action handlers */

	onOpenAttachDocumentModal(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		storeItem.type = DocumentModalIds.MANAGE_DOCUMENT; //TODO: não sei se é esse mesmo
		const attachDocumentData = {
			templateId: storeItem.template.id,
			fieldName: storeItem.inputName,
			gridIdentifier: storeItem.gridData.gridIdentifier,
			gridRowIndex: storeItem.gridData.indexOfDocumentInEdition
		};
		InputDocumentEventHandler.publish(InputDocumentEvents.ATTACH_DOCUMENT_MODAL_OPEN, attachDocumentData);
	},

	onAttachDocument(inputDocumentId, documentData) {
		const storeItem = this.store[inputDocumentId];
		storeItem.document = new Document(documentData);
		storeItem.selected = true;
		storeItem.waitingForAction = false;

		this.setDocumentNewValueInFormStore(inputDocumentId);
		this._setState(inputDocumentId);
	},

	/* Detach document action handlers */

	onDetachDocument(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		storeItem.type = DocumentModalIds.DETACH_DOCUMENT;
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());

		this.onWaitForAction(inputDocumentId);
		this.performDetachDocumentActionRequest(storeItem, processIds);
	},

	performDetachDocumentActionRequest(storeItem, processIds) {
		const url = DocumentActionRoutes.DETACH_DOCUMENT;
		const params = this.buildDetachDocumentRequestParams(storeItem, processIds);

		RestClient.post(url, params).then(
			this.handleDetachDocumentSuccessResponse.bind(this, storeItem.inputId),
			this.handleDocumentRequestOnModalErrorResponse.bind(this, storeItem.inputId)
		);
	},

	buildDetachDocumentRequestParams(storeItem, processIds) {
		const params = Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier,
			gridRowIndex: storeItem.gridData.indexOfDocumentInEdition,
			fieldName: storeItem.inputName,
			fileUniqueId: storeItem.document.fileUUID
		});

		return this.omitBy(params);
	},

	handleDetachDocumentSuccessResponse(inputDocumentId) {
		this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.DETACH_DOCUMENT);
		this.deselectDocument(inputDocumentId);
		this.setDocumentNewValueInFormStore(inputDocumentId, DocumentConstants.EMPTY_VALUE);
		this.handleDetachOrDeleteDocumentInGrid(inputDocumentId);

		const fieldApi = this.getFieldAPIForDocument(inputDocumentId);
		fieldApi.publish(FormEvents.UNLINKED_FILE_SUCCESS);
	},

	handleDetachOrDeleteDocumentInGrid(inputDocumentId) {
		const { gridData, inputProps } = this.store[inputDocumentId];

		if (gridData.rowIndex) {
			const gridFieldAPI = this.getFieldAPI(inputProps.formId, inputProps.gridFormParent);

			const dataRow = gridFieldAPI.dataRows(field => field.id === gridData.rowIndex)[0];
			const dataRowToUpdate = { ...dataRow, [inputDocumentId]: DocumentConstants.EMPTY_VALUE };

			gridFieldAPI.updateDataRow(dataRowToUpdate).apply();
		}
	},

	/* Delete document action handlers */

	onDeleteDocument(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());

		this.onWaitForAction(inputDocumentId);
		this.performDeleteDocumentActionRequest(storeItem, processIds);

		setTimeout(() => {
			document.activeElement && document.activeElement.focus();
			// let inputId = `input__${inputDocumentId}`;
			// let classInput = 'file-pathinput-text';
			// let btnCreateDoc = 'template-create-document';
			// let btnImportDoc = 'template-import-document';
			// $(`#${inputId} .${btnCreateDoc}`).length > 0 ? $(`#${inputId} .${btnCreateDoc}`)[0] && $(`#${inputId} .${btnCreateDoc}`)[0].focus() : $(`#${inputId} .${btnImportDoc}`)[0] && $(`#${inputId} .${btnImportDoc}`)[0].focus();
		}, 1000);
	},

	performDeleteDocumentActionRequest(storeItem, processIds) {
		const url = DocumentActionRoutes.DELETE_DOCUMENT;
		const params = this.buildDeleteDocumentRequestParams(storeItem, processIds);

		RestClient.post(url, params).then(
			this.handleDeleteDocumentSuccessResponse.bind(this, storeItem.inputId),
			this.handleDocumentRequestOnModalErrorResponse.bind(this, storeItem.inputId)
		);
	},

	buildDeleteDocumentRequestParams(storeItem, processIds) {
		const document = storeItem.document;

		return Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier,
			gridRowIndex: storeItem.gridData.indexOfDocumentInEdition,
			fieldName: storeItem.inputName,
			fileUniqueId: document.fileUUID
		});
	},

	handleDeleteDocumentSuccessResponse(inputDocumentId) {
		this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.DETACH_DOCUMENT);
		this.deselectDocument(inputDocumentId);
		this.handleDetachOrDeleteDocumentInGrid(inputDocumentId);

		const fieldApi = this.getFieldAPIForDocument(inputDocumentId);
		fieldApi.publish(FormEvents.DELETE_FILE_SUCCESS);
	},

	/* Handle select divergent document handlers */

	onSelectDivergentDocumentVersion(inputDocumentId) {
		this.onWaitForAction(inputDocumentId);
		this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.HANDLE_DIVERGENT_DOCUMENT_VERSIONS);
		this.selectDocument(inputDocumentId, this.buildDocumentDivergentVersionSelected(inputDocumentId));
	},

	buildDocumentDivergentVersionSelected(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const { document, divergentDocumentVersion } = storeItem;

		return Object.assign({}, document, divergentDocumentVersion);
	},

	/* Handle divergent versions document handlers */

	verifyDocumentVersionIsDivergent(inputDocumentId, locks) {
		return new Promise((resolve, reject) => {
			this.loadDocumentLastVersion(inputDocumentId)
			.then(data => this.handleDocumentDivergentVersionLoadSuccess(inputDocumentId, data, () => resolve(locks)))
			.catch(() => this.handleDocumentRequestOnModalErrorResponse(inputDocumentId));
		})
	},

	loadDocumentLastVersion(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());
		const params = this.buildLoadDocumentLastVersionRequestParams(storeItem, processIds);

		return RestClient.get(DocumentActionRoutes.GET_DOCUMENT_VERSIONS, params);
	},

	buildLoadDocumentLastVersionRequestParams(storeItem, processIds) {
		return Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier,
			gridRowIndex: storeItem.gridData.indexOfDocumentInEdition,
			fieldName: storeItem.inputName,
			documentId: storeItem.document.id,
			last: true
		});
	},

	handleDocumentDivergentVersionLoadSuccess(inputDocumentId, response, resolve) {
		const storeItem = this.store[inputDocumentId];
		const { documentVersionResults } = response.entity._embedded;
		const [ lastVersionDocument ] = documentVersionResults;

		if (this.isDivergentVersion(storeItem, lastVersionDocument.version))
			this.handleDocumentDivergentVersion(inputDocumentId, lastVersionDocument, storeItem);
		else
			resolve();
	},

	handleDocumentDivergentVersion(inputDocumentId, documentLastVersion, storeItem) {
		storeItem.type = DocumentModalIds.HANDLE_DIVERGENT_DOCUMENT_VERSIONS;
		storeItem.divergentDocumentVersion = documentLastVersion;

		this._setState(inputDocumentId);
	},

	isDivergentVersion(storeItem, lastVersionOfDocumentFromServer) {
		const { currentVersion } = storeItem.document.version;
		const parsedFloatCurrentVersion = parseFloat(currentVersion);

		return lastVersionOfDocumentFromServer > parsedFloatCurrentVersion;
	},

	/* Handle set form value in document */

	onSetValueInDocument(inputDocumentId, newValue) {
		if (!newValue) {
			this.deselectDocument(inputDocumentId);
		} else {
			this.store[inputDocumentId].value = newValue;
			this.onLoadDocumentWithValues(inputDocumentId);
		}
	},

	/* Handle document checkout has done */

	verifyDocumentCheckedoutBySameLoggedUser(inputDocumentId, locks) {
		return new Promise((resolve, reject) => {
			CurrentUserHandler.retrieveUserByTicket()
				.then(this.handleDocumentInCheckoutWithSameLoggedUser.bind(this, locks, () => resolve(locks), inputDocumentId))
				.catch(this.handleDocumentRequestOnModalErrorResponse.bind(this, inputDocumentId));
		});
	},

	handleDocumentInCheckoutWithSameLoggedUser(locks, resolvePromise, inputDocumentId, currentUser) {
		const includesEditionLock = locks.map(lock => lock.action).includes(DocumentLocks.EDITION);
		const isLocked = includesEditionLock ? this.documentIsLockedForEditionSameUser(locks, currentUser) : false;

		isLocked ? this.handleCheckoutDocumentSuccessResponse(inputDocumentId) : resolvePromise();
	},

	documentIsLockedForEditionSameUser(locks, currentUser) {
		const editionLock = locks.find(lock => lock.action === DocumentLocks.EDITION);
		return currentUser.login === editionLock.owner;
	},

	performDocumentCheckedoutByOtherUserVerification(inputDocumentId, locks) {
		return new Promise((resolve, reject) => {
			CurrentUserHandler.retrieveUserByTicket()
			.then(this.verifyDocumentCheckedoutByOtherUser.bind(this, locks, () => resolve(locks), inputDocumentId))
			.catch(this.handleDocumentRequestOnModalErrorResponse.bind(this, inputDocumentId));
		});
	},

	verifyDocumentCheckedoutByOtherUser(locks, resolvePromise, inputDocumentId, currentUser) {
		const hasEditionLock = locks.map(lock => lock.action).includes(DocumentLocks.EDITION);
		const documentIsLockedByOtherUser = hasEditionLock && !this.documentIsLockedForEditionSameUser(locks, currentUser);

		if (documentIsLockedByOtherUser)
			this.handleDocumentCheckedoutByOtherUser(inputDocumentId);
		else
			this.handleDocumentWithoutEditionLock(inputDocumentId, resolvePromise);
	},

	handleDocumentCheckedoutByOtherUser(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		storeItem.documentIsCheckedoutByOtherUser = true;
		storeItem.documentLoaded = false;

		this.displayDocumentLockedForCheckout(inputDocumentId);
	},

	handleDocumentWithoutEditionLock(inputDocumentId, resolvePromise) {
		const storeItem = this.store[inputDocumentId];
		storeItem.documentIsCheckedoutByOtherUser = false;
		resolvePromise();
	},

	parseLocksResponse(locksResponse) {
		const { entity } = locksResponse;
		return !!entity._embedded ? entity._embedded.documentLockResults : []
	},

	/* Handle duplicate document handlers */

	onSelectDuplicateDocument(inputDocumentId, document) {
		const storeItem = this.store[inputDocumentId];
		storeItem.selectedDuplicateDocument = document && document.id ? document : null;

		this.setDocumentNewValueInFormStore(inputDocumentId);
		this._setState(inputDocumentId);
	},

	onOverwriteDocument(inputDocumentId) {
		this.onWaitForAction(inputDocumentId);
		this.performDuplicateDocumentActionRequest(inputDocumentId, this.applyOverwriteDataToRequest);
	},

	onDuplicateDocument(inputDocumentId) {
		this.onWaitForAction(inputDocumentId);
		this.performDuplicateDocumentActionRequest(inputDocumentId, this.applyDuplicateDataToRequest);
	},

	applyOverwriteDataToRequest(url, params, inputDocumentId) {
		const selectedDocument = this.getFieldFromStoreItem(inputDocumentId, 'selectedDuplicateDocument');
		let options = {};

		if (url == DocumentActionRoutes.CREATE_DOCUMENT) {
			params = JSON.parse(params);
			Object.assign(params.data, {id: selectedDocument.id, version: selectedDocument.version, override: true});
		} else {
			params.append('version', selectedDocument.version);
			params.append('documentId', selectedDocument.id);
			params.append('override', true);
			options.multipart = true;
		}

		return {url, params, options};
	},

	applyDuplicateDataToRequest(url, params) {
		let options = {};

		try {
			params.append('duplicate', true);
			options.multipart = true;
		} catch {
			params = JSON.parse(params);
			params.data.duplicate = true;
		}

		return { url, params, options };
	},

	performDuplicateDocumentActionRequest(inputDocumentId, applyDataToRequest) {
		let request = this.getLastDuplicateDocumentRequest(inputDocumentId);
		let { url, params, options } = applyDataToRequest(request.url, request.params, inputDocumentId);

		RestClient.post(url, params, options)
			.then(this.handleDuplicateDocumentResponseSuccess.bind(this, inputDocumentId))
			.catch(this.handleDocumentRequestOnModalErrorResponse.bind(this, inputDocumentId));
	},

	handleDuplicateDocumentResponseSuccess(inputDocumentId, response) {
		const responseEntity = response.entity;
		const fieldApi = this.getFieldAPIForDocument(inputDocumentId);

		fieldApi.publish(FormEvents.DUPLICATED_DOCUMENTS_SUCCESS);

		if(responseEntity.hasOwnProperty('content')){
			let responseContent = response.entity.content;
			const importedDocumentData = {
				fileName: responseContent.fileName,
				fileUUID: responseContent.fileUniqueId,
				id: responseContent.documentId,
				version: responseContent.version
			};

			this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.HANDLE_DUPLICATE_DOCUMENT);
			this.selectDocument(inputDocumentId, importedDocumentData);

		} else {
			let responseContent = response.entity;
			const importedDocumentData = {
				fileName: responseContent.fileName,
				fileUUID: responseContent.fileUUID,
				id: responseContent.documentId,
				version: responseContent.version
			};

			this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.HANDLE_DUPLICATE_DOCUMENT);
			this.selectDocument(inputDocumentId, importedDocumentData);
		}
	},

	onErrorDuplicatedDocuments(inputDocumentId, modalId, response) {
		const [ responseError ] = response.entity.errors;
		this.onOpenDocumentModal(inputDocumentId, modalId);
		this.store[inputDocumentId].duplicateDocuments = responseError.content.duplicates;
		this.handleDuplicateDocumentResponse(inputDocumentId, modalId, response);
	},

	handleValidationsResponseErrors(inputDocumentId, modalId, response, importMultiple) {
		const [ responseError ] = response.entity.errors;
		const fieldApi = this.getFieldAPIForDocument(inputDocumentId);

		this.store[inputDocumentId].documentUploading = false;
		this.store[inputDocumentId].documentUploaded = false;

		if (modalId === DocumentModalIds.IMPORT_DOCUMENT && !this.store[inputDocumentId].openImportModal && !importMultiple) {
			this.onOpenImportDocumentModal(inputDocumentId);
		}

		switch(responseError.code) {
			case DocumentValidationsErrors.DUPLICATED_DOCUMENTS:
				fieldApi.publish(FormEvents.DOCUMENT_IMPORT_ERROR, DocumentValidationsErrors.DUPLICATED_DOCUMENTS, response);

				if (!importMultiple) {
					this.store[inputDocumentId].duplicateDocuments = responseError.content.duplicates;
					this.handleDuplicateDocumentResponse(inputDocumentId, modalId, response);
				}
				break;
			case DocumentValidationsErrors.DIVERGENT_VERSION_IN_CHECKIN:
				fieldApi.publish(FormEvents.DOCUMENT_IMPORT_ERROR, DocumentValidationsErrors.DIVERGENT_VERSION_IN_CHECKIN, response);

				if (!importMultiple) {
					this.handleCheckinLastVersionFound(inputDocumentId);
				}
				break;
			case DocumentValidationsErrors.CHECKOUT_UNDONE:
				fieldApi.publish(FormEvents.DOCUMENT_IMPORT_ERROR, DocumentValidationsErrors.CHECKOUT_UNDONE, response);

				if (!importMultiple) {
					this.handleCheckoutUndone(inputDocumentId, response);
				}
				break;
			default:
				fieldApi.publish(FormEvents.DOCUMENT_IMPORT_ERROR, response, response);

				if (!importMultiple) {
					this.handleDocumentRequestOnModalErrorResponse(inputDocumentId, response);
				}
		}
	},

	handleCheckoutUndone(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());
		const { fileUUID } = storeItem.document;

		this.undoCheckoutAndLoadDocument(storeItem, fileUUID, processIds);
		this.onDisplayFlashMessage(inputDocumentId, {
			type: 'error',
			message: Realize.i18n.t('template.messages.handleCheckoutUndone')
		});
	},

	handleCheckinLastVersionFound(inputDocumentId) {
		this.setWaitingForActionByInputIDocumentId(inputDocumentId, false);
		this.verifyDocumentVersionIsDivergent(inputDocumentId);
	},

	handleDuplicateDocumentResponse(inputDocumentId, modalId, response) {
		this.setLastDuplicateDocumentRequest(response.request, inputDocumentId);
		this.handleDuplicateModalOpening(inputDocumentId);
		this.setDuplicatedDocumentsFromResponse(inputDocumentId, response);
		this._setState(inputDocumentId);
	},

	handleDuplicateModalOpening(inputDocumentId) {
		this.store[inputDocumentId].type = DocumentModalIds.HANDLE_DUPLICATE_DOCUMENT;
		this.setWaitingForActionByInputIDocumentId(inputDocumentId, false);
	},

	setDuplicatedDocumentsFromResponse(inputDocumentId, response) {
		const [ error ] = response.entity.errors;
		const { duplicates } = error.content;
		const storeItem = this.store[inputDocumentId];
		storeItem.duplicateDocuments = this.parseDuplicateDocumentsFromResponse(duplicates);
	},

	setLastDuplicateDocumentRequest(request, inputDocumentId) {
		const { entity: params, path: url } = request;
		const storeItem = this.store[inputDocumentId];

		storeItem.lastDuplicateDocumentRequest = {params, url};
	},

	getLastDuplicateDocumentRequest(inputDocumentId) {
		return this.getFieldFromStoreItem(inputDocumentId, 'lastDuplicateDocumentRequest');
	},

	parseDuplicateDocumentsFromResponse(duplicates) {
		return duplicates.map(it => new DuplicatedDocument(it));
	},

	/* Cancel check-out in document action handlers */

	onCancelCheckoutDocument(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());

		this.onWaitForAction(inputDocumentId);
		this.performCancelCheckoutDocumentActionRequest(storeItem, processIds);
	},

	performCancelCheckoutDocumentActionRequest(storeItem, processIds) {
		const url = DocumentActionRoutes.CANCEL_CHECKOUT_DOCUMENT;
		const params = this.buildCancelCheckoutDocumentRequestParams(storeItem, processIds);

		RestClient.post(url, params).then(
			this.handleCancelCheckoutDocumentSuccessResponse.bind(this, storeItem),
			this.handleDocumentRequestOnModalErrorResponse.bind(this, storeItem.inputId)
		);
	},

	buildCancelCheckoutDocumentRequestParams(storeItem, processIds) {
		return Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier,
			gridRowIndex: storeItem.gridData.indexOfDocumentInEdition,
			fieldName: storeItem.inputName,
			documentId: storeItem.document.id
		});
	},

	handleCancelCheckoutDocumentSuccessResponse(storeItem) {
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());
		const { fileUUID } = storeItem.document;

		this.undoCheckoutAndLoadDocument(storeItem, fileUUID, processIds);
	},

	undoCheckoutAndLoadDocument(storeItem, fileUUID, processIds) {
		storeItem.submitData = {};
		storeItem.documentDownloaded = false;
		storeItem.document.removeLock(DocumentLocks.EDITION);
		ManageRequests.updateRequestRemoveFileLocks('fileUniqueId', storeItem.document._fileUUID, DocumentLocks.EDITION);
		this.performLoadDocumentActionRequest(storeItem, fileUUID, processIds);
	},

	/* Check-out document action handlers */

	onCheckoutDocument(inputDocumentId) {
		this.loadDocumentLocks(inputDocumentId)
		.then(this.verifyDocumentVersionIsDivergent.bind(this, inputDocumentId))
		.then(this.verifyDocumentCheckedoutBySameLoggedUser.bind(this, inputDocumentId))
		.then(this.performDocumentCheckedoutByOtherUserVerification.bind(this, inputDocumentId))
		.then(this.executeCheckoutDocument.bind(this, inputDocumentId))
		.catch(this.handleDocumentRequestOnModalErrorResponse.bind(this, inputDocumentId));
	},

	executeCheckoutDocument(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());

		this.onWaitForAction(inputDocumentId);
		this.performCheckoutDocumentActionRequest(storeItem, processIds);
	},

	performCheckoutDocumentActionRequest(storeItem, processIds) {
		const url = DocumentActionRoutes.CHECKOUT_DOCUMENT;
		const params = this.buildCheckoutDocumentRequestParams(storeItem, processIds);

		RestClient.post(url, params).then(
			this.handleCheckoutDocumentSuccessResponse.bind(this, storeItem.inputId),
			this.handleDocumentRequestOnModalErrorResponse.bind(this, storeItem.inputId)
		);
	},

	buildCheckoutDocumentRequestParams(storeItem, processIds) {
		const params = Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier,
			gridRowIndex: storeItem.gridData.indexOfDocumentInEdition,
			fieldName: storeItem.inputName,
			documentId: storeItem.document.id,
			version: storeItem.document.version.currentVersion
		});

		return this.omitBy(params);
	},

	handleCheckoutDocumentSuccessResponse(inputDocumentId) {
		this._waitForAction(inputDocumentId, false);
		CurrentUserHandler.retrieveUserByTicket().then(this.afterCheckoutSuccess.bind(this, inputDocumentId));
	},

	afterCheckoutSuccess(inputDocumentId, user) {
		this.store[inputDocumentId].documentCheckedOut = true;
		const { document } = this.store[inputDocumentId];

		document.addLock({ action: DocumentLocks.EDITION, owner: user.login });
		ManageRequests.updateRequestEntityFileLocks('fileUniqueId', document._fileUUID, { action: DocumentLocks.EDITION, owner: user.login });
		this.setCheckinDocumentModalInitialState(inputDocumentId);
	},

	/* Check-in document action handlers */

	onCheckinDocument(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const { fields } = storeItem.template;
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());

		if (DocumentMetadataValidator.validate(fields, DocumentSubmitDataContainers.CHECKIN, inputDocumentId)) {
			this.onWaitForAction(inputDocumentId);
			this.performCheckinDocumentActionRequest(storeItem, processIds);
		}
	},

	checkinUrlType(gridIdentifier, gridRowIndex, fieldName, documentId) {

		if(gridIdentifier && gridRowIndex && fieldName && documentId)
			return`/grids/${gridIdentifier}/rows/${gridRowIndex}/fields/${fieldName}/documents/${documentId}/checkin`;
		if(gridIdentifier && fieldName && documentId)
			return `/grids/${gridIdentifier}/fields/${fieldName}/documents/${documentId}/checkin`;
		if(fieldName && documentId)
			return `/fields/${fieldName}/documents/${documentId}/checkin`;
	},

	performCheckinDocumentActionRequest(storeItem, processIds) {
		const params = this.buildCheckinDocumentRequestParams(storeItem, processIds);
		const currentProcess = ProcessAPI.currentProcess();
		const url = DocumentActionRoutes.CHECKIN_DOCUMENT(currentProcess.lecomBpmUrl(), params, this.checkinUrlType);

		RestClient.post(url, params.data, {multipart: true})
			.then(this.handleCheckinDocumentSuccessResponse.bind(this, storeItem.inputId))
			.catch(this.handleValidationsResponseErrors.bind(this, storeItem.inputId, DocumentModalIds.MANAGE_DOCUMENT));
	},

	handleCheckinDocumentSuccessResponse(inputDocumentId, response) {
		let checkinDocumentData = response.entity;

		if(response.entity && response.entity.content)  {
			checkinDocumentData = response.entity.content;

			if(checkinDocumentData && checkinDocumentData.fileUniqueId) {
				checkinDocumentData.fileUUID = checkinDocumentData.fileUniqueId;
			}
		}

		const storeItem = this.store[inputDocumentId];
		storeItem.documentCheckedOut = false;

		this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.MANAGE_DOCUMENT);
		this.selectDocument(inputDocumentId, checkinDocumentData);
	},

	buildCheckinDocumentRequestParams(storeItem, processIds) {
		const params = Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier,
			gridRowIndex: storeItem.gridData.indexOfDocumentInEdition,
			fieldName: storeItem.inputName,
			documentId: storeItem.document.id,
			data: this.buildCheckinDocumentSubmitData(storeItem)
		});

		return this.omitBy(params);
	},

	buildCheckinDocumentSubmitData(storeItem) {
		const containerData = storeItem.submitData[DocumentSubmitDataContainers.CHECKIN];

		if(containerData.metadata) {
			containerData.metadata = Array.isArray(containerData.metadata)
				? containerData.metadata.filter(fieldData => typeof fieldData.value !== 'undefined' && fieldData.value !== null)
				: [];
		}

			const params = Object.assign({}, containerData, {
				templateId: storeItem.template.id,
				fileName: containerData.file && containerData.file.name
			});

			return this.omitBy(params);

	},

	/* Grid data event listeners */

	subscribeToGridEvents() {
		if (!this.initialized) {
			FormEventHandler.subscribe(FormEvents.GRID_EDIT, this.updateGridData.bind(this));
			FormEventHandler.subscribe(FormEvents.GRID_RESET, this.resetGridFormFieldValues.bind(this));
		}
	},

	updateGridData(eventType, formId, gridName, dataRowId, dataRow) {
		let storeItems = this.findStoreItemsByGrid(gridName)
		for (var cont = 0, len = storeItems.length; cont < len; cont++) {
			let storeItem = storeItems[cont];

			if (Object.keys(dataRow).includes(storeItem.inputId)) {
				storeItem.gridData.rowIndex = dataRowId

				if (!!storeItem.value) {
					storeItem.selected = true;
				}
			}
		}
	},

	resetGridFormFieldValues(eventType, formId, gridName) {
		this.findStoreItemsByGrid(gridName)
			.forEach(storeItem => storeItem.gridData.rowIndex = null);
	},

	findStoreItemsByGrid(gridName) {
		return Object.values(this.store)
		.filter(storeItem => storeItem.inputProps.gridFormParent === gridName);
	},

	/* Form data change event listeners */

	subscribeToFormEvents(storeItem) {
		if (!this.initialized) {
			const formApi = FormAPI.form(storeItem.inputProps.formId);
			this.handleFormInitialValuesLoadSuccess(null,null,formApi.initialValues());
		}
	},

	subscribeToProcessSaveEvent(storeItem) {
		ActivityFormEventHandler.subscribe(ActivityFormEvents.ACTIVITY_FORM_SAVE_SUCCESS, (_, data) => {
			const changedTemplateFields = data.entity.content.filter((field) => field.type === 'TEMPLATE')

			if (changedTemplateFields.length === 0) {
				return
			}

			const formId = storeItem.inputProps.formId
			const formAPI = FormAPI.form(formId)

			changedTemplateFields.forEach((template) => {
				formAPI.fields(template.field).value(template.value)
			})
		})
	},

	handleFormInitialValuesLoadSuccess(eventName, formStoreItemId, initialValuesResponse) {
		if(initialValuesResponse){
			initialValuesResponse
				.map(({ fieldId, gridId, value }) => ({ gridId, fieldId, properties: { value } }))
				.forEach(this.handleFormSetFieldPropertiesEvent.bind(this, eventName, formStoreItemId));
		}
	},

	handleFormSetFieldPropertiesEvent(eventName, formStoreItemId, eventProps) {
		if (eventProps && eventProps.properties && typeof eventProps.properties === 'object' && eventProps.properties.hasOwnProperty('value'))
			this.handleFormSetFieldProperties(eventProps.gridId, eventProps.fieldId, eventProps.properties.value);
	},

	handleFormSetFieldProperties(gridId, fieldName, newValue) {
		(Object.keys(this.store).includes(fieldName))
			? this.handleDocumentSetValue(fieldName, newValue)
			: this.handleFormFieldSetValue(gridId, fieldName, newValue);
	},

	handleDocumentSetValue(fieldName, fieldValue) {
		const storeItem = this.store[fieldName];

		if (storeItem.value !== fieldValue) {
			storeItem.value = fieldValue;
			this.onLoadDocumentWithValues(fieldName);
			this.onLoadTemplate(fieldName, storeItem.template.id);
		}
	},

	handleFormFieldSetValue(gridId, fieldName, fieldValue) {
		!!gridId
			? this.handleGridFormFieldSetValue(gridId, fieldName, fieldValue)
			: this.handleFormFieldOutsideGridSetValue(fieldName, fieldValue);
	},

	handleGridFormFieldSetValue(gridId, fieldName, fieldValue) {
		var storeItems = [];
		for (let key in this.store) {
			let storeItem = this.store[key];
			for (let cont = 0, len = storeItem.inputProps.formFieldMappings.length; cont < len; cont++) {
				let formFieldMappings = storeItem.inputProps.formFieldMappings[cont];

				if (formFieldMappings.formField.gridFormParent === gridId) {
					storeItems.push(storeItem);

					break;
				}
			}
		}

		this.setMetadataValueInStoreItems(storeItems, fieldName, fieldValue);
	},

	handleFormFieldOutsideGridSetValue(fieldName, fieldValue) {
		var storeItems = [];
		for (let key in this.store) {
			let storeItem = this.store[key];
			for (let cont = 0, len = storeItem.inputProps.formFieldMappings.length; cont < len; cont++) {
				let formFieldMappings = storeItem.inputProps.formFieldMappings[cont];

				if (typeof formFieldMappings.formField.gridFormParent === 'undefined' || formFieldMappings.formField.gridFormParent === null) {
					storeItems.push(storeItem);
					break;
				}
			}
		}

		this.setMetadataValueInStoreItems(storeItems, fieldName, fieldValue);
	},

	setMetadataValueInStoreItems(storeItems, fieldName, fieldValue) {
		for (var cont = 0, len = storeItems.length; cont < len; cont++) {
			let storeItem = storeItems[cont];
			this.setFormFieldsAssociatedToDocument(fieldName, fieldValue, storeItem);

			if (storeItem.document.isNew()) {
				this.setDocumentFormFieldValue(fieldName, fieldValue, storeItem);
			}
		}
	},

	setFormFieldsAssociatedToDocument(fieldName, fieldValue, storeItem) {

		this.filterMappedFormField(storeItem, fieldName)
			.map(mapping => [mapping.documentField, fieldValue])
			.forEach(([documentField, fieldValue]) => {
				storeItem.metadataFieldsAssociationValues[documentField] = fieldValue
			})
	},

	setDocumentFormFieldValue(fieldName, fieldValue, storeItem) {
		const { document } = storeItem;

		this.filterMappedFormField(storeItem, fieldName)
			.forEach(mapping => document.setFieldValue(mapping.documentField, fieldValue))
	},

	filterMappedFormField(storeItem, fieldName) {
		const { formFieldMappings } = storeItem.inputProps;

		return formFieldMappings.filter(mapping => mapping.formField.name == fieldName)
	},

	/* Document modal action handlers */

	onOpenManageDocumentModal(inputDocumentId) {
		setTimeout(() => {
			let idModal = $('#documentModal');
			let buttonClose = $('.button-close-modal-document');
			let buttonCheckout = $('#btn-document-checkout');

			buttonClose.attr('tabindex', 0);
			buttonCheckout.attr('tabindex', 0);

			if(idModal.is(':visible') === true){
				idModal.keydown((e) => {
					if(e.which === 27){
						buttonClose[0].click();
					}
				})
			}
		}, 1000);

		const storeItem = this.store[inputDocumentId];
		storeItem.type = DocumentModalIds.MANAGE_DOCUMENT;
		const { documentReadonly, documentLoaded, document } = storeItem;

		if (!documentLoaded || documentReadonly)
			this.onLoadDocument(inputDocumentId, document.fileUUID)
				.then(() => this.setManageDocumentModalInitialState(inputDocumentId));
		else
			this.setManageDocumentModalInitialState(inputDocumentId);

		this.onOpenDocumentModal(inputDocumentId, DocumentModalIds.MANAGE_DOCUMENT);
	},

	onOpenCreateDocumentModal(inputDocumentId) {
		setTimeout(() => {
			let idModal = $('#documentModal');
			let autoCompleteDefault = $('#autocomplete_select_undefined');
			let autoCompleteSpan = $('#documentModal #input__undefined .caret');

			let buttonClose = $('.button-close-modal-document');
			let buttonCreateDoc = $('.button-create-document');

			autoCompleteDefault.attr('tabindex', '-1');
			autoCompleteSpan.attr('tabindex', '0');

			autoCompleteSpan.keydown((e) => {
				if(e.which === 13){
					autoCompleteSpan[0].click();
				}
			});

			idModal.keydown((e) => {
				if(e.which ===  27 && $(e.target).parents('.input-autocomplete__result').length  === 0){
					buttonClose[0].click();
				}
			});

			buttonClose.attr('tabindex', '0');
			buttonCreateDoc.attr('tabindex', '0');

			buttonClose.keydown(function(e) {
				e.which === 13 && buttonClose[0].click();
			});

			if(buttonCreateDoc.attr('disabled') === undefined){
				buttonCreateDoc.keydown(function(e) {
					e.which === 13 && e.currentTarget.click()

					if(e.shiftKey){
						if(e.shiftKey && e.keyCode === 9)
							return true
					}

					if(e.which === 9 || e.keyCode === 9 ){
						// autoCompleteSpan.focus()
						$('#documentModal .modal-header span').focus();
						return false
					}
				});
			} else  if(buttonCreateDoc.attr('disabled') !== undefined){
				buttonCreateDoc.keydown(function(e) {
					if(e.shiftKey){
						if(e.shiftKey && e.keyCode === 9)
							return true
					}

					if(e.which === 9 || e.keyCode === 9 ){
						// autoCompleteSpan.focus();
						$('#documentModal .modal-header span').focus();
						return false
					}
				});
			}
		}, 1000);

		this.store[inputDocumentId].type = DocumentModalIds.CREATE_DOCUMENT;
		this._setState(inputDocumentId);
		this.setMetadataSubmitValuesFromAssociationValues(inputDocumentId, DocumentSubmitDataContainers.CREATE);
		this.onOpenDocumentModal(inputDocumentId, DocumentModalIds.CREATE_DOCUMENT);
	},

	onOpenImportDocumentModal(inputDocumentId) {
		setTimeout(() => {
			$('#documentModal').keydown((e) => {
				if(e.which ===  27){
					$('.button-close-modal-document')[0].click();
				}
			});

			$(".btn-import-document").attr("tabindex", 0)
			$('.inputImportDocument').removeAttr('tabindex');

			let buttonCloseVisible = $('#documentModal .button-close-modal-document').is(':visible');
			let buttonImportVisible = $('#documentModal .button-import-document').is(':visible');

			if(buttonCloseVisible)
				$('.button-close-modal-document')[0].setAttribute('tabindex', '0');
			else if(buttonImportVisible)
				$('.button-import-document')[0].setAttribute('tabindex', '0');

			$('.button-close-modal-document').keypress(function(e) {
				e.which === 13 && e.currentTarget.click();
			});
		}, 1000)

		this.store[inputDocumentId].type = DocumentModalIds.IMPORT_DOCUMENT;
		this.store[inputDocumentId].openImportModal = true;

		if(Object.keys(this.store[inputDocumentId].document.fieldValues).length > 0)
			this.store[inputDocumentId].document.fieldValues = {}

		this._setState(inputDocumentId);

		setTimeout(() => {
			this.setMetadataSubmitValuesFromAssociationValues(inputDocumentId, DocumentSubmitDataContainers.IMPORT);
		}, 1000);

		this.onOpenDocumentModal(inputDocumentId, DocumentModalIds.IMPORT_DOCUMENT);
	},

	onOpenDetachDocumentModal(inputDocumentId) {
		const openDetachDocumentModal = () => this.loadDocumentCanBeDeleted(inputDocumentId).then(() => {
			this.store[inputDocumentId].type = DocumentModalIds.DETACH_DOCUMENT;
			this.onWaitForAction(inputDocumentId, false);
			this.onOpenDocumentModal(inputDocumentId, DocumentModalIds.DETACH_DOCUMENT);
		});

		this._waitForAction(inputDocumentId)
			.then(openDetachDocumentModal);

	},

	/* Document digital signature */

	onOpenDigitalSignature(inputDocumentValue) {
		window.parent.postMessage({
			event: 'DIGITAL_SIGNATURE',
			fileUniqueId: inputDocumentValue.fileUUID
		}, '*');
	},

	onDownloadDigitalSignature() {
		const currentProcess = ProcessAPI.currentProcess();
		const lecomBpmUrl = currentProcess.lecomBpmUrl();
		const lastCharacterUrl = lecomBpmUrl.slice(-1);
		const digitalSignatureUrl = `${lastCharacterUrl === '/' ? '' : '/'}bpm/signer/AssinadorDigital.msi`;

		window.open(`${lecomBpmUrl}${digitalSignatureUrl}`, '_blank');
	},

	getNameRowsGrid(inputDocumentId, inputDocumentValue){
		const storeItem = this.store[inputDocumentId];
		storeItem.digitalSignature.fileNameRowGrid = inputDocumentValue && inputDocumentValue.fileName;
	},

	getUUIDRowsGrid(inputDocumentId, inputDocumentValue){
		const storeItem = this.store[inputDocumentId];
		storeItem.digitalSignature.fileUUIDRowGrid = inputDocumentValue && inputDocumentValue.fileUUID;
	},

	validatePermissionDigitalSignature(inputDocumentId){
		const storeItem = this.store[inputDocumentId];
		const currentProcess = ProcessAPI.currentProcess();
		const gatewayUrl = currentProcess.gatewayUrl();
		const url = DocumentActionRoutes.GET_PERMISSION_DIGITAL_SIGNATURE(gatewayUrl);

		RestClient.get(url)
			.then(res => {
				let permission = res.entity.permission;
				if(permission === true){
					storeItem.digitalSignature.noDigitalSign = false;
					this.findServicePort(inputDocumentId, storeItem.digitalSignature.countIdPort);
				} else {
					storeItem.digitalSignature.notPermssionDigitalSign = false;
				}
			})
			.catch(err => {
				console.log('erro validatePermissionDigitalSignature');
				storeItem.digitalSignature.errorDigitalSignature = true;
				this.onWaitForAction(inputDocumentId, false);
			});
	},

	findServicePort(inputDocumentId, id){
		const storeItem = this.store[inputDocumentId];
		let date = new Date().getTime();
		let url = storeItem.digitalSignature.localServiceAddress + ":" + storeItem.digitalSignature.portsToTest[id] + storeItem.digitalSignature.signerPingContext + date + ".png";
		this.verifyImage(inputDocumentId, url);
	},

	verifyImage(inputDocumentId, url, type) {
	    let timeout = 2000;
		let timedOut = false, timer;
		let img = new Image();

	    img.onerror = img.onabort = function() {
	        if (!timedOut) {
				clearTimeout(timer);

				if (type === 'verifyCompatibility') {
					this.checkStatus(inputDocumentId, "errorCompatibility");
				}
				else
	            	this.checkStatus(inputDocumentId, "error");
	        }
		}.bind(this);

	    img.onload = function() {
	        if (!timedOut) {
				clearTimeout(timer);
				if (type === "verifyToAsigner") {
					this.checkStatus(inputDocumentId, type);
				}
				else if (type === "verifyCompatibility") {
					this.checkStatus(inputDocumentId, "successCompatibility");
				}
				else
	            	this.checkStatus(inputDocumentId, "success");
	        }
		}.bind(this);

	    img.src = url;
	    timer = setTimeout(function() {
			timedOut = true;
			if (type === false) {

			}
			else
				this.checkStatus(inputDocumentId, "timeout");
	    }.bind(this), timeout);
	},

	checkStatus(inputDocumentId, type) {
		const storeItem = this.store[inputDocumentId];
		let port = storeItem.digitalSignature.countIdPort

		if (type === "verifyToAsigner") {
			storeItem.digitalSignature.noDigitalSign = false;
			storeItem.digitalSignature.connectingDigitalSignService = true;
		}
		else if(type === "success") {
			storeItem.digitalSignature.noDigitalSign = false;
			storeItem.digitalSignature.connectingDigitalSignService = true;
			let urlVerifyCompatibility = `${storeItem.digitalSignature.localServiceAddress}:${storeItem.digitalSignature.portsToTest[port]}${storeItem.digitalSignature.signerCheckcompatibilityContext}`;
			this.verifyImage(inputDocumentId, urlVerifyCompatibility, "verifyCompatibility");
		}
		else if(type === "successCompatibility") {
			this.getSignActionToken(inputDocumentId, port, type);
		}
		else if (type === 'errorCompatibility') {
			storeItem.digitalSignature.errorCompatibility = true;
			this.onWaitForAction(inputDocumentId, false);
		}
		else {
			this.failCallBack(inputDocumentId);
		}
	},

	failCallBack(inputDocumentId){
		const storeItem = this.store[inputDocumentId];

		if(storeItem.digitalSignature.countIdPort < storeItem.digitalSignature.portsToTest.length){
			storeItem.digitalSignature.countIdPort = storeItem.digitalSignature.countIdPort +1;

			this.findServicePort(inputDocumentId, storeItem.digitalSignature.countIdPort);
			storeItem.digitalSignature.noDigitalSign = true;
		}
		else {
			if(storeItem.digitalSignature.countIdPort === storeItem.digitalSignature.portsToTest.length){
				storeItem.digitalSignature.buttonDownloadDigitalSignature = true;
				this.onWaitForAction(inputDocumentId, false);
			}
			this.onWaitForAction(inputDocumentId, false);
		}
	},

	getSignActionToken(inputDocumentId, port, type){
		const storeItem = this.store[inputDocumentId];
		const currentProcess = ProcessAPI.currentProcess();
		const gatewayUrl = currentProcess.gatewayUrl();
		let fileUUID = storeItem.document.fileUUID || storeItem.digitalSignature.fileUUIDRowGrid;

		const url = DocumentActionRoutes.POST_CREATE_TOKEN_DIGITAL_SIGNATURE(gatewayUrl, fileUUID);

		RestClient.post(url)
			.then(res => {
				let tokenData = res.entity;
				this.invokeSignerByImage(inputDocumentId, port, type, tokenData);
			})
			.catch(err => {
				console.log('error getSignActionToken');
			});
	},

	invokeSignerByImage(inputDocumentId, port, type, tokenData){
		const storeItem = this.store[inputDocumentId];
		let currentKey = tokenData.key;
		let portDigitalSign = storeItem.digitalSignature.portsToTest[port];

		if(currentKey === undefined) {
			storeItem.digitalSignature.signatureFailure = true;
			this.onWaitForAction(inputDocumentId, false);
		} else {
			const currentProcess = ProcessAPI.currentProcess();
			const gatewayUrl = currentProcess.gatewayUrl();

			let ecmUrl = () => {
				let url = `${gatewayUrl}/ecmcore/`;
				let subs1 = url.split("-").join("-0");
				let subs2 = subs1.split(":").join("-1");
				let subs3 = subs2.split("/").join("-2");
				return subs3;
			}

			let fileName = storeItem.document.fileName;
			const gridFormParent = storeItem.inputProps.gridFormParent;

			if (gridFormParent)
				fileName = storeItem.digitalSignature.fileNameRowGrid;

			let loginUser = 'adm'; //VALIDAR
			let date = new Date().getTime();
			let keyUrlDigitalSign = `${storeItem.digitalSignature.localServiceAddress}:${portDigitalSign}${storeItem.digitalSignature.signerInvokeContext}/${ecmUrl()};${currentKey};${loginUser};${fileName};${date}.png`;

			this.verifyImage(inputDocumentId, keyUrlDigitalSign, "verifyToAsigner");

			if(type === 'successCompatibility'){
				this.waitForSigner(inputDocumentId, currentKey);
			}
			else {
				storeItem.digitalSignature.signatureFailure = true;
			}
		}
	},

	waitForSigner(inputDocumentId, currentKey){
		const storeItem = this.store[inputDocumentId];
		const currentProcess = ProcessAPI.currentProcess();
		const gatewayUrl = currentProcess.gatewayUrl();
		const url = DocumentActionRoutes.GET_STATUS_DIGITAL_SIGNATURE(gatewayUrl, currentKey);

		setTimeout(() => {
			RestClient.get(url)
				.then(res => {
					let status = res.entity.status;
					let data = res.entity;

					if(status === 'DOWNLOADED_FILE'){
						storeItem.digitalSignature.waitingForTheSignedDocument = true;
						storeItem.digitalSignature.connectingDigitalSignService = false;
						storeItem.digitalSignature.canceledDigitalSignature = false;

						this.onWaitForAction(inputDocumentId, false);
						this.waitForSigner(inputDocumentId, currentKey);
					}
					else if(status === 'UPLOADED_SIGNED'){
						let newFileUniqueId = res.entity.newFileUniqueId;
						storeItem.document.fileUUID = newFileUniqueId;
						storeItem.digitalSignature.documentUploadedSignature = true;
						storeItem.digitalSignature.connectingDigitalSignService = false;
						storeItem.digitalSignature.waitingForTheSignedDocument = false;

						this.uploadedSignedCallback(inputDocumentId, data);
						this.onWaitForAction(inputDocumentId, false);
						this.waitForSigner(inputDocumentId, currentKey);
					}
					else if(status === 'CANCELED'){
						storeItem.digitalSignature.documentUploadedSignature = false;
						storeItem.digitalSignature.waitingForTheSignedDocument = false;
						storeItem.digitalSignature.connectingDigitalSignService = false;
						storeItem.digitalSignature.canceledDigitalSignature = true;

						this.onWaitForAction(inputDocumentId, false);
						this.waitForSigner(inputDocumentId, currentKey);
					}
				})
				.catch(err => {
					storeItem.digitalSignature.canceledDigitalSignature = false;
					storeItem.digitalSignature.connectingDigitalSignService = false;
					// storeItem.digitalSignature.signatureFailure = true;
				});

		}, 1000);
	},

	uploadedSignedCallback(inputDocumentId, data){
		const storeItem = this.store[inputDocumentId];
		const formId = storeItem.inputProps.formId;
		const formAPI = FormAPI.form(formId);
		let gridFormParent = storeItem.inputProps.gridFormParent;

		if(gridFormParent !== null){
			if(storeItem.document.fileName === undefined || storeItem.document.id === undefined){
				let inputId = storeItem.inputId;
				let rowsGrid = formAPI.grids(gridFormParent).dataRows();

				rowsGrid.forEach((i) => {
					if(i[inputId] !== null){
						let verifyId = i[inputId].includes(storeItem.digitalSignature.fileUUIDRowGrid);
						if(verifyId){
							let idRowGrid = i.id;
							let splitName = i[inputId].split(':');
							let nameFileRowGrid = splitName[0];

							Form.grids(gridFormParent).updateDataRow({ id: idRowGrid , [inputDocumentId]: `${nameFileRowGrid}:${data.newFileUniqueId}`}).apply();
							// storeItem.document.fileName = nameFileRowGrid;
							this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.DIGITAL_SIGNATURE_DOCUMENT);
							return
						}
					}
				});
			}
			else {
				formAPI.grids(gridFormParent).fields(inputDocumentId).value(`${data.fileName}:${data.newFileUniqueId}`).apply();
				this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.DIGITAL_SIGNATURE_DOCUMENT);
			}
		}
		else {
			formAPI.fields(inputDocumentId).value(`${data.fileName}:${data.newFileUniqueId}`).apply();
			this.addLockAtosBPM(inputDocumentId, data.newFileUniqueId);
		}
	},

	addLockAtosBPM(inputDocumentId, fileUniqueId){
		const storeItem = this.store[inputDocumentId];

		const formId = storeItem.inputProps.formId;
		const formAPI = FormAPI.form(formId);

		const currentProcess = ProcessAPI.currentProcess();
		const processInstanceData = currentProcess.processInstanceData();
		const gatewayUrl = currentProcess.gatewayUrl();

		let gridId = storeItem.inputProps.gridFormParent;

		if(gridId !== null){
			let rowsGrid = formAPI.grids(gridId).dataRows();
			let filterRowsGrid = rowsGrid.filter(i => i[inputDocumentId].includes(storeItem.digitalSignature.fileUUIDRowGrid));
			let idRowGrid = filterRowsGrid[0].id;
			let url = DocumentActionRoutes.PUT_LOOCK_GRID_ROW_DOCUMENT(gatewayUrl, processInstanceData, gridId, idRowGrid, inputDocumentId, fileUniqueId);

			RestClient.put(url)
				.then(res => { this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.DIGITAL_SIGNATURE_DOCUMENT) })
				.catch(err => { console.log(err) });

		}
		else {
			let url = DocumentActionRoutes.PUT_LOOCK_FIELD_DOCUMENT(gatewayUrl, processInstanceData, inputDocumentId, fileUniqueId);

			RestClient.put(url)
				.then(res => { this.onCloseDocumentModal(inputDocumentId, DocumentModalIds.DIGITAL_SIGNATURE_DOCUMENT) })
				.catch(err => { console.log(err) });
		}
	},

	// FIM DO ASSINADOR

	onOpenDocumentModal(inputDocumentId, documentModalId, callback) {
		ModalActions.open({ modalId: "documentModal" }, callback);

		if(documentModalId === 'digital-signature-modal'){
			const storeItem = this.store[inputDocumentId];

			storeItem.digitalSignature.openModalSignature = true;
			this.onWaitForAction(inputDocumentId, false);
		}
	},

	onCloseDocumentModal(inputDocumentId, documentModalId) {
		if(documentModalId === 'create-document-modal'){
			$('.button-create-document').off('keydown');

			let idField = `input__${inputDocumentId}`;
			let createDoc = 'template-create-document';

			$(`#${idField} .${createDoc}`)[0].focus();
		}

		else if(documentModalId === 'import-document-modal'){
			$(".btn-import-document").off('keydown');
			$('.button-import-document').off('keydown');

			let inputId = `#input__${inputDocumentId}`
			let idField = `input__${inputDocumentId}`;
			let importDoc = 'template-import-document';
			let classButtonEdit = 'template-edit-document';
			let classButtonDetach = 'template-desvincular-documento';

			$('.inputImportDocument').val('');

			setTimeout(() => {
				if($(`${inputId} input`).val() === ""){
					if ($(`#${idField} .${importDoc}`)[0]) {
						$(`#${idField} .${importDoc}`)[0].focus();
					}
				}
				else {
					let inputBtnEditVisible = $(`#${idField} .${classButtonEdit}`).is(':visible');

					if(inputBtnEditVisible === true) {
						if ($(`#${idField} .${classButtonEdit}`)[0]) {
							$(`#${idField} .${classButtonEdit}`)[0].focus();
						}
					}
					else {
						if ($(`#${idField} .${classButtonDetach}`)[0]) {
							$(`#${idField} .${classButtonDetach}`)[0].focus();
						}
					}
				}
			}, 500);
		}

		else if(documentModalId === 'detach-document-modal'){
			let idField = `input__${inputDocumentId}`;
			let detach = 'template-desvincular-documento';
			let createDoc = 'template-create-document';

			$(`#${idField} .${detach}`).length > 0 ? $(`#${idField} .${detach}`)[0].focus() : $(`#${idField} .${createDoc}`)[0].focus();
		}

		else if(documentModalId === 'manage-document-modal'){
			let idField = `input__${inputDocumentId}`;
			let classButtonEdit = 'template-edit-document';

			$(`#${idField} .${classButtonEdit}`)[0].focus();
		}

		else if(documentModalId === 'handle-duplicate-document-modal'){
			let idField = $(`#input__${inputDocumentId}`);
			let divButtons = $(`#input__${inputDocumentId} .document-action-buttons a`);

			divButtons[0].focus();
		}

		const storeItem = this.store[inputDocumentId];
		const modalProps = { modalId: "documentModal" };

		if(documentModalId === 'digital-signature-modal'){
			const storeItem = this.store[inputDocumentId];
			storeItem.digitalSignature.documentUploadedSignature = false;
			storeItem.digitalSignature.errorDigitalSignature = false;
			this.onWaitForAction(inputDocumentId, false);
		}

		this.disposeDocumentModal(documentModalId, storeItem, inputDocumentId);
		return new Promise((resolve, reject) => ModalActions.close(modalProps, this.onCloseModalCallback.bind(this, inputDocumentId, resolve)));
	},

	disposeDocumentModal(documentModalId, storeItem, inputDocumentId) {
		const isManageModal = documentModalId === DocumentModalIds.MANAGE_DOCUMENT;
		const isDivergentModal = documentModalId === DocumentModalIds.HANDLE_DIVERGENT_DOCUMENT_VERSIONS;
		const isDetachModal = documentModalId === DocumentModalIds.DETACH_DOCUMENT;

		if (!isManageModal && !isDivergentModal && !isDetachModal)
			storeItem.document.fieldValues = Object.assign({}, storeItem.metadataFieldsAssociationValues);

		storeItem.metadataValidationErrors = [];
		storeItem.documentFileSelected = false;
		storeItem.openImportModal = false;

		if ($("#importMultipleModal").css("display") === 'none' && $("#importMultipleGridModal").css("display") === 'none') {
			storeItem.submitData = {};
		}

		if (documentModalId == 'digital-signature-modal') {
			storeItem.digitalSignature.countIdPort = 0;
			storeItem.digitalSignature.noDigitalSign = false;
			storeItem.digitalSignature.buttonDownloadDigitalSignature = false;
			storeItem.digitalSignature.errorCompatibility = false;
			storeItem.digitalSignature.openModalSignature = false;
			this.onWaitForAction(inputDocumentId, false);
		}
	},

	onCloseModalCallback(inputDocumentId, callback) {
		this.clearSubmissionData(inputDocumentId);
		this.onDismissFlashMessage(inputDocumentId);

		if (typeof callback == "function")
			callback();
	},

	/* Document modal flash message action handlers */

	onDisplayFlashMessage(inputDocumentId, message) {
		const storeItem = this.store[inputDocumentId];
		storeItem.flashMessage = message;
		storeItem.waitingForAction = false;
		this._setState(inputDocumentId);
	},

	onDismissFlashMessage(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		storeItem.flashMessage = null;
		this._setState(inputDocumentId);
	},

	displayDocumentReadonlyMessage(inputDocumentId) {
		this.onDisplayFlashMessage(inputDocumentId, {
			type: 'warning',
			message: Realize.i18n.t('template.messages.documentReadonly')
		});
	},

	displayDocumentLockedForCheckout(inputDocumentId) {
		this.onDisplayFlashMessage(inputDocumentId, {
			message: Realize.i18n.t('template.messages.documentLockedForCheckout'),
			type: 'error'
		});
	},

	/* Private; Document check delete loaders */

	loadDocumentCanBeDeleted(inputDocumentId) {
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());
		const storeItem = this.store[inputDocumentId];

		return this.performCheckDocumentCanBeDeletedActionRequest(storeItem, processIds);
	},

	performCheckDocumentCanBeDeletedActionRequest(storeItem, processIds) {
		const url = DocumentActionRoutes.CHECK_DOCUMENT_CAN_BE_DELETED;
		const params = this.buildCheckCanBeDeletedRequestParams(storeItem, processIds);

		return new Promise((resolve, reject) => {
			RestClient.get(url, params).then(
				this.handleCheckDocumentCanBeDeletedSuccessResponse.bind(this, storeItem, resolve),
				this.handleDocumentRequestOnModalErrorResponse.bind(this, storeItem.inputId)
			);
		});
	},

	buildCheckCanBeDeletedRequestParams(storeItem, processIds) {
		return Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier,
			gridRowIndex: storeItem.gridData.indexOfDocumentInEdition,
			fieldName: storeItem.inputName,
			documentId: storeItem.document.id
		});
	},

	handleCheckDocumentCanBeDeletedSuccessResponse(storeItem, resolve, response) {
		const { canDelete } = response.entity;

		storeItem.documentCanBeDeleted = canDelete;
		this._setState(storeItem.inputDocumentId);
		resolve(response);
	},

	/* Private: Document locks data loaders */

	loadDocumentLocks(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const processIds = serializePrivateProperties(ProcessAPI.currentProcess().processIds());

		this.onWaitForAction(inputDocumentId);
		return this.performLoadDocumentLocksActionRequest(storeItem, processIds);
	},

	performLoadDocumentLocksActionRequest(storeItem, processIds) {
		const url = DocumentActionRoutes.GET_DOCUMENT_LOCKS;
		const params = this.buildLoadDocumentLocksRequestParams(storeItem, processIds);

		return new Promise((resolve) => {
			RestClient.get(url, params).then(
				this.handleLoadDocumentLocksSuccessResponse.bind(this, storeItem.inputId, resolve),
				this.handleDocumentRequestOnModalErrorResponse.bind(this, storeItem.inputId)
			);
		});
	},

	buildLoadDocumentLocksRequestParams(storeItem, processIds) {
		return Object.assign({}, processIds, {
			gridIdentifier: storeItem.gridData.gridIdentifier,
			gridRowIndex: storeItem.gridData.indexOfDocumentInEdition,
			fieldName: storeItem.inputName,
			documentId: storeItem.document.id
		});
	},

	handleLoadDocumentLocksSuccessResponse(inputDocumentId, resolve, response) {
		const storeItem = this.store[inputDocumentId];
		storeItem.document.locks = this.parseDocumentLocksResponse(response);
		storeItem.waitingForAction = false;

		resolve(storeItem.document.locks);
	},

	parseDocumentLocksResponse(response) {
		const responseEntity = response.entity;
		return !!responseEntity._embedded ? responseEntity._embedded.documentLockResults : [];
	},

	/* Private: Document selection and deselection response handlers */

	deselectDocument(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		storeItem.document = new Document({fieldValues: Object.assign({}, storeItem.metadataFieldsAssociationValues)});
		storeItem.selected = false;
		storeItem.waitingForAction = false;
		storeItem.documentLoaded = false;

		this.setDocumentNewValueInFormStore(inputDocumentId);
		this._setState(inputDocumentId);
	},

	selectDocument(inputDocumentId, documentData, modalIdToCloseAfterSelect) {
		const storeItem = this.store[inputDocumentId];
		storeItem.document = new Document(documentData);
		storeItem.selected = true;
		storeItem.waitingForAction = false;

		this.onLoadDocument(inputDocumentId, documentData.fileUUID, modalIdToCloseAfterSelect);
		this.setDocumentNewValueInFormStore(inputDocumentId);
		this._setState(inputDocumentId);
	},

	/* Private: Document request error handlers */

	handleDocumentRequestOnModalErrorResponse(inputDocumentId, response) {
		this.onDisplayFlashMessage(inputDocumentId, {
			message: ResponseHandler.parseErrorResponseMessage(response, 'template.messages'),
			type: 'error'
		});
	},

	handleDocumentRequestErrorResponse(inputDocumentId, response) {
		const storeItem = this.store[inputDocumentId];
		storeItem.waitingForAction = false;

		this._setState(inputDocumentId);
		ResponseHandler.displayErrorResponseFlashMessage(response);
	},

	/* Private: submission data handlers */

	clearSubmissionData(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		storeItem.selectedDuplicateDocument = null;
		storeItem.documentDownloaded = false;

		if ($("#importMultipleModal").css("display") === 'none' && $("#importMultipleGridModal").css("display") === 'none') {
			storeItem.submitData = {};
		}
	},

	/* Private: WaitingForAction handlers */

	setWaitingForActionByInputIDocumentId(inputDocumentId, newValue) {
		const storeItem = this.store[inputDocumentId];
		storeItem.waitingForAction = newValue;
	},

	/* Private: Common getter for prop in storeItem */

	getFieldFromStoreItem(inputDocumentId, field) {
		const storeItem = this.store[inputDocumentId];
		return storeItem[field];
	},

	/* Private: Update new value of document in FormStore */

	setDocumentNewValueInFormStore(inputDocumentId, newValue) {
		const storeItem = this.store[inputDocumentId];
		const fieldApi = this.getFieldAPIForDocument(inputDocumentId);
		const value = newValue || storeItem.value || '';

		fieldApi.publish(FormEvents.CHANGE, value);
		fieldApi.value(value);
	},

	setManageDocumentModalInitialState(inputDocumentId) {
		const storeItem = this.store[inputDocumentId];
		const { documentReadonly } = storeItem;

		documentReadonly
		? this.displayDocumentReadonlyMessage(inputDocumentId)
		: this.setCheckinDocumentModalInitialState(inputDocumentId);
	},

	setCheckinDocumentModalInitialState(inputDocumentId) {
		this.setCheckinDocumentModalVersionInitialState(inputDocumentId);
		this._setState(inputDocumentId);
		this.setMetadataSubmitValuesFromFieldValues(inputDocumentId, DocumentSubmitDataContainers.CHECKIN);
	},

	setCheckinDocumentModalVersionInitialState(inputDocumentId) {
		const { document, submitData } = this.store[inputDocumentId];
		submitData[DocumentSubmitDataContainers.CHECKIN] = {};
		submitData[DocumentSubmitDataContainers.CHECKIN].version = document.version.minorVersion;
	},

	/* Form API Helpers */

	getFieldAPIForDocument(inputDocumentId) {
		const { inputProps } = this.store[inputDocumentId];
		return this.getFieldAPI(inputProps.formId, inputProps.id, inputProps.gridFormParent);
	},

	getFieldAPI(formId, id, gridFormParent) {
		const formAPI = FormAPI.form(formId);

		return (gridFormParent)
		? formAPI.fields(gridFormParent).fields(id)
		: formAPI.fields(id);
	},

	omitBy(params) {
		var objReturn = {};

		for (var key in params) {
			if (params[key])
				objReturn[key] = params[key];
		}

		return objReturn;
	},

	onPreviewDocumentFile(fileUniqueId) {
		const currentProcess = ProcessAPI.currentProcess();
		const processIds = currentProcess.processIds();
		const params = { ...processIds, fileUniqueId };
		const path = `${HOST_NAME_API}/form/view-public?processInstances=${params.processInstanceId || ''}&activityInstances=${params.activityInstanceId || ''}&cycles=${params.cycle || ''}&fileUniqueId=${params.fileUniqueId || ''}`;

		if (currentProcess.isTestMode()) {
			const testUser = getCookie("LecomEnvironmentMode");
			window.open(`${path}&testUser=${testUser || ''}`, '_blank');
		}
		else if (currentProcess.isPublic()) {
			const formUuid = (currentProcess && currentProcess.uuid()) ? currentProcess.uuid() : localStorage.getItem("uuid");
			window.open(`${path}&formUuid=${formUuid || ''}`, '_blank');
		}
		else {
			const url = DocumentActionRoutes.PREVIEW_DOCUMENT(currentProcess.lecomBpmUrl(), params);
			window.open(url, '_blank');
		}
	},

	onSetMetadataFromAssociation(inputDocumentId, contextMetadata) {
		this.setMetadataSubmitValuesFromAssociationValues(inputDocumentId, DocumentSubmitDataContainers.IMPORT, contextMetadata);
	},

	onSetFileUniqueIdInDocument(inputDocumentId, fileUniqueId) {
		const storeItem = this.store[inputDocumentId];
		storeItem.document.fileUUID = fileUniqueId;

		this.setDocumentNewValueInFormStore(inputDocumentId, `${storeItem.document.fileName}:${fileUniqueId}`);
		this._setState(inputDocumentId);

		window.currentDocumentIdSign = null;
	},

	onDownloadFileAndSetupAI(inputDocumentId, inputDocumentValue, callback) {
		const storeItem = this.store[inputDocumentId];
		const currentProcess = ProcessAPI.currentProcess();
		const processIds = currentProcess.processIds();
		const ticket = currentProcess.ticket();
		const language = currentProcess.language();
		const params = this.buildDownloadDocumentFileRequestParams(storeItem, processIds, inputDocumentValue);
		const url = DocumentActionRoutes.DOWNLOAD_DOCUMENT(currentProcess.lecomBpmUrl(), processIds, params.gridIdentifier, params.fileUniqueId);

		FileDownloaderUtils.download(url, params, inputDocumentValue.fileName, "GET", "application/octet-stream", "blob", (response) => {
			const iframeAi = document.getElementById("iframe-ai-web");

			iframeAi.contentWindow.postMessage({
				event: 'SEND_SETUP',
				initialData: {
					ticket,
					language,
                    fileSummary: {
                    	blob: response.entity,
                        id: inputDocumentValue ? inputDocumentValue.fileUUID : "",
                        name: inputDocumentValue ? inputDocumentValue.fileName : ""
                    },
					context: "DOCUMENT_SUMMARY"
				}
			}, '*');

			if (callback) {
				callback();
			}
		});
	}
});

export default DocumentStore;
